// filepath: apps/web/src/features/lists/PaginationBar.tsx
//
// FR-UI-LIST-1 AC-3 — keyset pagination chrome for the list view.
//
// Pairs with useKeysetPagination. Renders:
//   - A "Next" <button> enabled iff `next_cursor !== ""`.
//   - NO "Previous" button — keyset pagination is forward-only per
//     FR-QRY-1 AC-2 + ARCHITECTURE_WEB.md §5.
//   - A page-size label read from the server's `page_size` field
//     (FR-QRY-1 AC-2 — server-enforced page size).
//
// INVARIANTS:
//   - INV-9: zero dangerouslySetInnerHTML. All strings render as text nodes.
//   - FR-UI-CORE-9 / WCAG 2.1 AA: the Next button carries a clear
//     aria-label and a disabled state when there is no next page.

import React from "react";
import { useTranslation } from "@/i18n/useTranslation";

export interface PaginationBarProps {
  /** Server's page_size from the response envelope. */
  pageSize: number;
  /** True iff a next page exists (`next_cursor !== ""`). */
  hasNextPage: boolean;
  /** Called when the user clicks Next. */
  onNext: () => void;
  /** Optional explicit aria-label override. */
  ariaLabel?: string;
}

/**
 * PaginationBar — keyset forward-only pagination chrome.
 */
export function PaginationBar({
  pageSize,
  hasNextPage,
  onNext,
  ariaLabel,
}: PaginationBarProps): React.ReactElement {
  const { t } = useTranslation();
  const navLabel = ariaLabel ?? t("lists.pagination.aria_label", "Pagination");
  // Page-size label: i18next interpolates {{pageSize}} from the options
  // bag. (Previously this resolved the key then ran a manual
  // .replace("{pageSize}") — that only worked while the bundle kept the
  // single-brace placeholder, which was the i18next single-brace bug
  // fixed in ISS-f4fa1b7b-274.)
  const pageSizeLabel = t(
    "lists.pagination.page_size",
    "Page size: {{pageSize}}",
    {
      pageSize,
    },
  );

  return (
    <nav
      className="entity-list-pagination"
      data-testid="entity-list-pagination"
      aria-label={navLabel}
    >
      <button
        type="button"
        className="entity-list-pagination-next"
        data-testid="entity-list-pagination-next"
        onClick={onNext}
        disabled={!hasNextPage}
        aria-disabled={hasNextPage ? "false" : "true"}
      >
        {t("lists.pagination.next", "Next")}
      </button>
      <span
        className="entity-list-pagination-page-size"
        data-testid="entity-list-pagination-page-size"
      >
        {pageSizeLabel}
      </span>
    </nav>
  );
}
